import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    useTheme,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";

const InventoryCategoryManager = () => {
    const theme = useTheme();
    const [categories, setCategories] = useState([]);
    const [newCategoryName, setNewCategoryName] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const API = "https://figazuresql.swancreate.com/api/swa/FINANCE_InventoryCategories";

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const resp = await axios.get(API);
            const data = resp.data.map((c) => ({
                id: c.apiid,
                apiid: c.apiid,
                InventoryCategoryID: c.InventoryCategoryID,
                InventoryCategoryName: c.InventoryCatgegoryName,
            }));
            setCategories(data);
            setError(null);
        } catch (e) {
            console.error("Failed to load categories", e);
            setError("Failed to load categories.");
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (!newCategoryName.trim()) return;
        try {
            await axios.post(API, { InventoryCatgegoryName: newCategoryName });
            setNewCategoryName("");
            fetchCategories();
        } catch (e) {
            console.error("Failed to add category", e);
            setError("Failed to add category.");
        }
    };

    const handleDelete = async (apiid) => {
        try {
            await axios.delete(`${API}/${apiid}`);
            setCategories((prev) => prev.filter((c) => c.apiid !== apiid));
        } catch (e) {
            console.error("Failed to delete category", e);
            setError("Failed to delete category.");
        }
    };

    const columns = [
        {
            field: "InventoryCategoryName",
            headerName: "Category Name",
            flex: 1,
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<DeleteIcon fontSize="small" />}
                    label="Delete"
                    onClick={() => handleDelete(params.id)}
                    sx={{ color: theme.palette.error.main }}
                    key="delete"
                />,
            ],
        },
    ];

    return (
        <Box
            p={2}
            sx={{
                backgroundColor: theme.palette.mode === "dark" ? "#121212" : "#fff",
                color: theme.palette.mode === "dark" ? "#E0E0E0" : "#000",
                borderRadius: 1,
            }}
        >
            <Typography variant="h5" gutterBottom>
                Manage Inventory Categories
            </Typography>

            <Box display="flex" alignItems="center" mb={2} gap={2}>
                <TextField
                    label="New Category"
                    variant="outlined"
                    size="small"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    sx={{
                        backgroundColor: theme.palette.mode === "dark" ? "#1E1E1E" : "#fff",
                        input: { color: theme.palette.mode === "dark" ? "#E0E0E0" : "#000" },
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.mode === "dark" ? "#444" : undefined,
                        },
                    }}
                />
                <Button variant="contained" onClick={handleAdd}>
                    Add
                </Button>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <Box
                    height={300}
                    sx={{
                        "& .MuiDataGrid-root": {
                            backgroundColor: theme.palette.mode === "dark" ? "#1E1E1E" : "#fafafa",
                            color: theme.palette.mode === "dark" ? "#E0E0E0" : "#000",
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#333" : "#ddd"}`,
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: theme.palette.mode === "dark" ? "#2A2A2A" : "#f5f5f5",
                            color: theme.palette.mode === "dark" ? "#E0E0E0" : "#000",
                            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#333" : "#ddd"}`,
                        },
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: theme.palette.mode === "dark" ? "#181818" : "#fff",
                        },
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                            backgroundColor: theme.palette.mode === "dark" ? "#242424" : "#f9f9f9",
                        },
                        "& .MuiDataGrid-footerContainer": {
                            backgroundColor: theme.palette.mode === "dark" ? "#2A2A2A" : "#f5f5f5",
                            borderTop: `1px solid ${theme.palette.mode === "dark" ? "#333" : "#ddd"}`,
                        },
                        // color the action (trash) icon
                        "& .MuiGridActionsCellItem-root .MuiSvgIcon-root": {
                            color: theme.palette.error.main,
                        },
                    }}
                >
                    <DataGrid
                        rows={categories}
                        columns={columns}
                        disableSelectionOnClick
                        hideFooter
                        getRowId={(row) => row.apiid}
                    />
                </Box>
            )}
        </Box>
    );
};

export default InventoryCategoryManager;
